import type {
  FootballLegacyAliasParams,
  FootballLegacyAliasResolution,
  FootballMatchDetailResponse,
} from '@ballpeek/api/client'

import {
  footballLegacyAliasResponseSchema,
  footballMatchDetailResponseSchema,
} from '@ballpeek/api/client'
import * as Sentry from '@sentry/react'

import { getApiBaseUrl } from '~/lib/api-client'
import {
  PublicFootballRequestError,
  readPublicFootballResponse,
} from '~/lib/public-football-read'

export type LegacyMatchAliasSourceKind = FootballLegacyAliasParams['sourceKind']
export type LegacyMatchAliasResolution
  = | FootballLegacyAliasResolution
    | { disposition: 'unresolved' }
    | { disposition: 'collision' }

function recordFailure(response: Response, message: string): void {
  if (response.ok)
    return
  Sentry.addBreadcrumb({
    category: 'api_call',
    message,
    level: 'error',
    data: {
      api_request_id: response.headers.get('X-Request-Id'),
      status: response.status,
    },
  })
}

export async function fetchFootballMatchDetail(
  matchId: string,
  signal?: AbortSignal,
): Promise<FootballMatchDetailResponse> {
  const response = await fetch(`${getApiBaseUrl()}/api/football/matches/${encodeURIComponent(matchId)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  })
  recordFailure(response, 'Football Match Detail request failed')
  const payload = await readPublicFootballResponse(
    response,
    footballMatchDetailResponseSchema,
    'Football Match Detail request failed',
  )
  if (payload.data.match.matchId !== matchId)
    throw new Error('Football Match Detail response does not match the requested canonical fixture')
  return payload
}

export async function fetchLegacyMatchAlias(
  sourceKind: LegacyMatchAliasSourceKind,
  sourceId: string,
  signal?: AbortSignal,
): Promise<LegacyMatchAliasResolution> {
  const response = await fetch(
    `${getApiBaseUrl()}/api/football/match-aliases/${sourceKind}/${encodeURIComponent(sourceId)}`,
    {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    },
  )
  recordFailure(response, 'Legacy match alias request failed')
  try {
    const payload = await readPublicFootballResponse(
      response,
      footballLegacyAliasResponseSchema,
      'Legacy match alias request failed',
    )
    return payload.data
  }
  catch (error) {
    if (error instanceof PublicFootballRequestError && error.status === 404)
      return { disposition: 'unresolved' }
    if (error instanceof PublicFootballRequestError && error.status === 409)
      return { disposition: 'collision' }
    throw error
  }
}
